import React, { useRef, useState } from 'react';
import { Form, Input } from 'antd';
import { FormInstance } from 'antd/lib/form';
import './style.scss';
import SparkMD5 from 'spark-md5';
import { jumpToLogin } from '../../../lib';
import { putUserChangeApi } from '../../../../api';
import {
  messageError,
  messageSuccess,
  messageWarning,
} from '../../../../components/message';
import { CodeUser } from '../../../../model/api-modules';
import { ModalWrap } from '../../../../components/modal-wrap';
import { getPasswordRule } from '../../../utils/password';
import getFormatMessage from '../../../../locales/get-format-message';
import { reportPageError } from '../../../lib/bk-report';

interface IProps {
  readonly onClose: () => void;
  readonly username: string;
}
interface IResetPassword {
  readonly newPassword: string;
  readonly currentPassword: string;
  readonly rePassword: string;
}

export const EditPasswordModal = (props: IProps) => {
  const formRef = useRef<FormInstance<any>>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const md5 = (value: string) => {
    if (value) {
      return SparkMD5.hash(value);
    }
    return '';
  };

  const onConfirm = async (): Promise<boolean> => {
    return new Promise((resolve) => {
      if (formRef.current) {
        formRef.current
          .validateFields()
          .then(async (value: IResetPassword) => {
            const { currentPassword, newPassword } = value;

            setLoading(true);
            try {
              const resetRes = await putUserChangeApi(
                props.username,
                md5(currentPassword.trim()),
                md5(newPassword.trim())
              );
              if (resetRes?.code === 0) {
                messageSuccess(getFormatMessage('reset_password_success'));
                setTimeout(() => {
                  props.onClose();
                  jumpToLogin();
                  resolve(true);
                }, 1000);
              } else if (resetRes?.code === CodeUser.OldPasswordError) {
                messageError(getFormatMessage('old_password_try_again'));
              } else {
                messageError(getFormatMessage('reset_password_fail'));
              }
            } catch (err: any) {
              messageError(err?.apiError?.message);
            }
            setLoading(false);
          })
          .catch((err) => {
            resolve(false);
            console.log(err);
            setLoading(false);
            reportPageError(err);
          });
      } else {
        messageWarning(getFormatMessage('Identity_verification_expired'));
        resolve(false);
      }
    });
  };

  const onOk = async () => {
    onConfirm();
    return true;
  };

  const renderFormStep = () => {
    return (
      <Form
        ref={formRef}
        className="c-main-form"
        name="basic"
        initialValues={{ remember: true }}
        autoComplete="off"
        layout="vertical"
        colon={false}
        requiredMark={false}
      >
        <Form.Item
          label={getFormatMessage('current_password')}
          name="currentPassword"
          colon={false}
          rules={[
            {
              required: true,
              message: getFormatMessage('input_your_password'),
              type: 'string',
              whitespace: true,
            },
          ]}
          validateTrigger={'onBlur'}
        >
          <Input
            autoFocus
            type="password"
            className="c-antd-input-xl-main"
            placeholder={getFormatMessage('enter_your_password')}
            spellCheck="false"
          />
        </Form.Item>
        <Form.Item
          label={getFormatMessage('new_password')}
          name="newPassword"
          colon={false}
          rules={[
            {
              type: 'string',
              whitespace: true,
            },
            {
              validator: getPasswordRule(formRef),
            },
          ]}
          validateTrigger={'onBlur'}
        >
          <Input.Password
            className="c-antd-input-xl-main"
            placeholder={getFormatMessage('enter_new_password')}
            spellCheck="false"
          />
        </Form.Item>
        <Form.Item
          label={getFormatMessage('confirm_password')}
          name="rePassword"
          colon={false}
          rules={[
            {
              required: true,
              message: getFormatMessage('can_not_empty'),
              type: 'string',
              whitespace: true,
            },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue('newPassword') === value) {
                  return Promise.resolve();
                }
                return Promise.reject(
                  new Error(getFormatMessage('passwords_not_match'))
                );
              },
            }),
          ]}
          validateTrigger={'onBlur'}
        >
          <Input.Password
            type="password"
            className="c-antd-input-xl-main"
            placeholder={getFormatMessage('please_repeat_password')}
            spellCheck="false"
          />
        </Form.Item>
      </Form>
    );
  };

  return (
    <ModalWrap
      width="480px"
      onOk={onOk}
      onClose={props.onClose}
      title={getFormatMessage('password')}
      className="edit-password-modal"
      cancelText={getFormatMessage('cancel')}
      oKText={getFormatMessage('save')}
      loading={loading}
      maskClosable={false}
    >
      {renderFormStep()}
    </ModalWrap>
  );
};
